import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { parseMatchTime } from "../../utils/dateUtils";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS, getRoleColor } from "./adminStyles";

const API_URL = getBaseURL();

const UpdatePointsInner = () => {
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState("");
  const [matchDetails, setMatchDetails] = useState(null);
  const [team1Players, setTeam1Players] = useState([]);
  const [team2Players, setTeam2Players] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else fetchMatches();
  }, [token, navigate]);

  const fetchMatches = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/matches/live-upcoming`, { headers: { Authorization: `Bearer ${token}` } });
      setMatches(res.data);
    } catch (err) { toast.error("Failed to load matches."); }
  };

  const fetchPlayers = async (matchId, mDetails) => {
    try {
      const res = await axios.get(`${API_URL}/admin/players/${matchId}`, { headers: { Authorization: `Bearer ${token}` } });
      const playersData = res.data.map(p => ({ ...p, newPoints: p.points || 0 }));
      setTeam1Players(playersData.filter(p => p.team === mDetails.team1));
      setTeam2Players(playersData.filter(p => p.team === mDetails.team2));
    } catch (err) { toast.error("Failed to load players."); }
  };

  const handleMatchSelect = (e) => {
    const matchId = e.target.value;
    setSelectedMatch(matchId);
    if (!matchId) return;
    const mDetails = matches.find(m => m.match_id === matchId);
    setMatchDetails(mDetails);
    fetchPlayers(matchId, mDetails);
  };

  const handlePointChange = (playerId, val, teamStr) => {
    const parsedVal = parseFloat(val.replace(/[^0-9.]/g, "")) || 0;
    if (teamStr === "team1") {
      setTeam1Players(team1Players.map(p => p.player_id === playerId ? { ...p, newPoints: parsedVal } : p));
    } else {
      setTeam2Players(team2Players.map(p => p.player_id === playerId ? { ...p, newPoints: parsedVal } : p));
    }
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    const updates = [
      ...team1Players.map(p => ({ player_id: p.player_id, points: p.newPoints })),
      ...team2Players.map(p => ({ player_id: p.player_id, points: p.newPoints })),
    ];
    try {
      await axios.put(`${API_URL}/admin/players/points`, { updates }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("All player points updated successfully!");
    } catch (err) {
      toast.error("Failed to update points.");
    } finally { setIsSaving(false); }
  };

  const playing1 = team1Players.filter(p => p.is_playing);
  const playing2 = team2Players.filter(p => p.is_playing);

  const PlayerRow = ({ p, teamStr }) => {
    const rc = getRoleColor(p.role);
    return (
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 0",
        borderBottom: `1px solid ${COLORS.cardBorder}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {p.image ? (
            <img src={p.image} alt={p.player_name} style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover", border: `1px solid ${COLORS.cardBorder}` }} onError={e => { e.target.style.display = "none"; }} />
          ) : (
            <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: rc + "15", border: `1px solid ${rc}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}>
              {p.role === "BAT" ? "🏏" : p.role === "BOWL" ? "⚡" : p.role === "WK" ? "🧤" : "🌟"}
            </div>
          )}
          <div>
            <div style={{ fontSize: "13px", fontWeight: 700, color: COLORS.text }}>{p.player_name}</div>
            <span style={{ fontSize: "8px", fontWeight: 700, color: rc, background: rc + "15", border: `1px solid ${rc}30`, padding: "1px 6px", borderRadius: "4px", textTransform: "uppercase" }}>{p.role}</span>
          </div>
        </div>
        <input
          type="number"
          step="0.5"
          value={p.newPoints}
          onChange={e => handlePointChange(p.player_id, e.target.value, teamStr)}
          style={{
            width: "72px",
            padding: "8px 10px",
            background: COLORS.inputBg,
            border: `1px solid ${rc}40`,
            borderRadius: "8px",
            color: rc,
            fontSize: "16px",
            fontWeight: 700,
            textAlign: "center",
            outline: "none",
            fontFamily: "'Inter', sans-serif",
          }}
        />
      </div>
    );
  };

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>⚡ Live Point Updater</div>
          <div style={S.pageSubtitle}>Bulk Update Match Player Points</div>
        </div>
        {selectedMatch && (
          <button
            onClick={handleSaveAll}
            disabled={isSaving}
            className={isSaving ? "" : "admin-btn-hover"}
            style={{ ...S.btnPrimary, opacity: isSaving ? 0.7 : 1, cursor: isSaving ? "not-allowed" : "pointer", fontSize: "14px", padding: "12px 28px", transition: "all 0.15s" }}
          >
            {isSaving ? "Saving..." : "💾 Save All Points"}
          </button>
        )}
      </div>

      {/* Match Select */}
      <div style={{ ...S.card, marginBottom: "24px" }}>
        <label style={S.label}>Select Live / Upcoming Match</label>
        <select className="admin-input-focus" style={S.select} value={selectedMatch} onChange={handleMatchSelect}>
          <option value="">-- Choose Match --</option>
          {matches.map(m => (
            <option key={m.match_id} value={m.match_id}>
              {m.team1} vs {m.team2} · {parseMatchTime(m).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </option>
          ))}
        </select>
      </div>

      {selectedMatch && matchDetails && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", animation: "fadeIn 0.3s ease" }}>
          {/* Team 1 */}
          <div style={{ ...S.card, borderTop: `3px solid ${COLORS.roleBat}` }}>
            <div style={{ fontSize: "14px", fontWeight: 700, color: COLORS.roleBat, marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {matchDetails.team1}
              <span style={{ fontSize: "11px", color: COLORS.textMuted, fontWeight: 600 }}>{playing1.length} playing</span>
            </div>
            {playing1.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px", color: COLORS.textDim, fontSize: "12px" }}>Playing 11 not announced yet.</div>
            ) : (
              playing1.map(p => <PlayerRow key={p.player_id} p={p} teamStr="team1" />)
            )}
          </div>

          {/* Team 2 */}
          <div style={{ ...S.card, borderTop: `3px solid ${COLORS.green}` }}>
            <div style={{ fontSize: "14px", fontWeight: 700, color: COLORS.green, marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {matchDetails.team2}
              <span style={{ fontSize: "11px", color: COLORS.textMuted, fontWeight: 600 }}>{playing2.length} playing</span>
            </div>
            {playing2.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px", color: COLORS.textDim, fontSize: "12px" }}>Playing 11 not announced yet.</div>
            ) : (
              playing2.map(p => <PlayerRow key={p.player_id} p={p} teamStr="team2" />)
            )}
          </div>
        </div>
      )}

      {selectedMatch && (
        <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={handleSaveAll}
            disabled={isSaving}
            className={isSaving ? "" : "admin-btn-hover"}
            style={{ ...S.btnPrimary, opacity: isSaving ? 0.7 : 1, cursor: isSaving ? "not-allowed" : "pointer", fontSize: "15px", padding: "14px 40px", transition: "all 0.15s" }}
          >
            {isSaving ? "Saving..." : "💾 Save All Points"}
          </button>
        </div>
      )}
    </div>
  );
};

const UpdatePoints = () => (
  <ToastProvider>
    <UpdatePointsInner />
  </ToastProvider>
);

export default UpdatePoints;
